import { expandedVenues } from './expanded-venues';
export type CategoryId =
  | 'performance'
  | 'cinema'
  | 'sports'
  | 'air'
  | 'rail'
  | 'bus'
  | 'ship'
  | 'popup';

export type ImplementedCategoryId = 'performance' | 'cinema';

export interface CategoryDefinition {
  id: CategoryId;
  label: string;
  description: string;
  implemented: boolean;
  spaceLabel: string;
  instanceLabel: string;
}

export interface VenueSpace {
  id: string;
  name: string;
  aliases?: string[];
  format?: string;
  sectionSuggestions: string[];
}

export interface Venue {
  id: string;
  slug: string;
  categoryId: ImplementedCategoryId;
  name: string;
  region: string;
  addressHint: string;
  officialUrl: string;
  officialSeatmapUrl?: string;
  spaces: VenueSpace[];
  verifiedAt: string;
  source: 'official-directory' | 'user-entered';
}

export interface PerformanceInstance {
  id: string;
  venueId: string;
  spaceId: string;
  title: string;
  startsAt: string | null;
  stageNote: string | null;
  source: 'user-entered' | 'unknown';
}

export const categories: CategoryDefinition[] = [
  {
    id: 'performance',
    label: '공연·콘서트',
    description: '공연장과 홀, 회차를 기준으로 봐요.',
    implemented: true,
    spaceLabel: '홀·공연장',
    instanceLabel: '공연·회차',
  },
  {
    id: 'cinema',
    label: '영화',
    description: '영화관과 상영관, 포맷을 기준으로 봐요.',
    implemented: true,
    spaceLabel: '상영관',
    instanceLabel: '영화·상영 시간',
  },
  {
    id: 'sports',
    label: '스포츠',
    description: '경기장 좌석은 데이터 검증 뒤 열어요.',
    implemented: false,
    spaceLabel: '구장·구역',
    instanceLabel: '경기',
  },
  {
    id: 'air',
    label: '항공',
    description: '기재 변경을 안전하게 추적할 때 열어요.',
    implemented: false,
    spaceLabel: '기재',
    instanceLabel: '편명',
  },
  {
    id: 'rail',
    label: '철도',
    description: '열차 편성과 차량을 검증한 뒤 열어요.',
    implemented: false,
    spaceLabel: '차량',
    instanceLabel: '열차',
  },
  {
    id: 'bus',
    label: '버스',
    description: '차량 배치가 확인된 노선부터 열어요.',
    implemented: false,
    spaceLabel: '차량',
    instanceLabel: '노선·시간',
  },
  {
    id: 'ship',
    label: '선박',
    description: '선실 배치 검증 뒤 열어요.',
    implemented: false,
    spaceLabel: '선실·갑판',
    instanceLabel: '항차',
  },
  {
    id: 'popup',
    label: '그 밖의 지정석',
    description: '반복 수요와 배치 근거를 확인한 뒤 열어요.',
    implemented: false,
    spaceLabel: '공간',
    instanceLabel: '일정',
  },
];

/**
 * 공식 명칭 탐색을 돕는 최소 디렉터리예요. 좌석 배치 데이터는
 * seat-layouts.ts가 출처·확인일·정밀도와 함께 별도로 소유해요.
 */
export const starterVenues: Venue[] = [
  {
    id: 'blue-square',
    slug: 'blue-square',
    categoryId: 'performance',
    name: '블루스퀘어',
    region: '서울 용산구',
    addressHint: '한강진역',
    officialUrl: 'https://www.bluesquare.kr/',
    spaces: [
      { id: 'shinhan-card-hall', name: '우리은행홀 (구 신한카드홀)', aliases: ['신한카드홀'], sectionSuggestions: ['1층', '2층', '3층'] },
      { id: 'mastercard-hall', name: '우리WON뱅킹홀 (구 마스터카드홀)', aliases: ['마스터카드홀'], sectionSuggestions: ['스탠딩', '1층', '2층'] },
    ],
    verifiedAt: '2026-08-12',
    source: 'official-directory',
  },
  {
    id: 'sejong-center',
    slug: 'sejong-center',
    categoryId: 'performance',
    name: '세종문화회관',
    region: '서울 종로구',
    addressHint: '광화문역',
    officialUrl: 'https://www.sejongpac.or.kr/',
    spaces: [
      { id: 'grand-theater', name: '대극장', sectionSuggestions: ['1층', '2층', '3층'] },
      { id: 'm-theater', name: 'M씨어터', sectionSuggestions: ['1층', '2층'] },
      { id: 's-theater', name: 'S씨어터', sectionSuggestions: ['객석'] },
    ],
    verifiedAt: '2026-08-12',
    source: 'official-directory',
  },
  {
    id: 'cgv-yongsan',
    slug: 'cgv-yongsan',
    categoryId: 'cinema',
    name: 'CGV 용산아이파크몰',
    region: '서울 용산구',
    addressHint: '용산역',
    officialUrl: 'https://www.cgv.co.kr/',
    spaces: [
      { id: 'imax-laser', name: 'IMAX관', format: 'IMAX', sectionSuggestions: ['일반석'] },
      { id: '4dx-screen', name: '4DX SCREEN관', format: '4DX SCREEN', sectionSuggestions: ['일반석'] },
      { id: 'screen-direct', name: '상영관 번호 직접 입력', sectionSuggestions: ['일반석'] },
    ],
    verifiedAt: '2026-08-12',
    source: 'official-directory',
  },
  {
    id: 'lotte-cinema-worldtower',
    slug: 'lotte-cinema-worldtower',
    categoryId: 'cinema',
    name: '롯데시네마 월드타워',
    region: '서울 송파구',
    addressHint: '잠실역',
    officialUrl: 'https://www.lottecinema.co.kr/',
    spaces: [
      { id: 'superplex', name: 'SUPER PLEX', format: 'SUPER PLEX', sectionSuggestions: ['일반석'] },
      { id: 'charlotte', name: 'CHARLOTTE', format: 'CHARLOTTE', sectionSuggestions: ['일반석'] },
      { id: 'screen-direct', name: '상영관 번호 직접 입력', sectionSuggestions: ['일반석'] },
    ],
    verifiedAt: '2026-08-12',
    source: 'official-directory',
  },
];

starterVenues.push(...expandedVenues);

export function searchVenues(categoryId: ImplementedCategoryId, query: string): Venue[] {
  const needle = query.trim().toLocaleLowerCase('ko-KR');
  return starterVenues.filter((venue) => {
    if (venue.categoryId !== categoryId) return false;
    if (!needle) return true;
    return `${venue.name} ${venue.region} ${venue.addressHint}`.toLocaleLowerCase('ko-KR').includes(needle);
  });
}

export function venueById(id: string): Venue | undefined {
  return starterVenues.find((venue) => venue.id === id);
}

export function venueBySlug(slug: string): Venue | undefined {
  return starterVenues.find((venue) => venue.slug === slug);
}
